import { Button } from '@carbon/react';
import { Checkmark, Rocket, TaskView } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';

import { ActivityFeedPanelOpenButton } from 'components/activity-feed-panel';
import type { InterestWorkspaceResponse } from 'types/api-responses';

import {
  openPromoteModalAtom,
  tasksPanelOpenAtom,
  toggleTasksPanelAtom,
} from './atoms';

interface WorkspaceHeaderProps {
  workspace: InterestWorkspaceResponse;
}

/**
 * Action buttons rendered in the PageHeader `actions` slot for the workspace
 * shell: Activity (icon-only), Tasks {n/n}, and Promote / Promoted.
 *
 * Activity opens the shared `ActivityFeedPanel` drawer (rendered by the page).
 * Tasks toggles the Tasks side panel via `toggleTasksPanelAtom`. Promote opens
 * the Promote modal via `openPromoteModalAtom`, then swaps to a disabled
 * "Promoted" state (Checkmark icon, `Promoted` label) when `workspace.promoted`
 * flips true after a successful promotion. All three route through the overlay
 * coordinator so opening one closes every other workspace overlay (INS-9897).
 *
 * @param {WorkspaceHeaderProps} props - Component props.
 * @param {InterestWorkspaceResponse} props.workspace - The loaded workspace
 *   record. The workspace has ONE task — "Certify asset interest" — complete
 *   when the workspace is promoted (mirrors the Type Curve verification
 *   step); `promoted` also toggles the Promote/Promoted state.
 * @return {JSX.Element} The rendered action button group.
 */
export const WorkspaceHeader = ({
  workspace,
}: WorkspaceHeaderProps): JSX.Element => {
  const openPromoteModal = useSetAtom(openPromoteModalAtom);
  const tasksOpen = useAtomValue(tasksPanelOpenAtom);
  const toggleTasksPanel = useSetAtom(toggleTasksPanelAtom);
  return (
    <>
      <ActivityFeedPanelOpenButton tooltipPosition="bottom" />
      <Button
        kind="ghost"
        size="md"
        renderIcon={TaskView}
        aria-pressed={tasksOpen}
        onClick={() => toggleTasksPanel()}
      >
        Tasks {workspace.promoted ? 1 : 0}/1
      </Button>
      <Button
        kind="primary"
        size="md"
        renderIcon={workspace.promoted ? Checkmark : Rocket}
        disabled={workspace.promoted}
        onClick={() => openPromoteModal()}
      >
        {workspace.promoted ? 'Promoted' : 'Promote'}
      </Button>
    </>
  );
};
